import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  toggleVatBatchChecklistItem,
  toggleLuxBatchChecklistItem,
  submitVatBatch,
  submitLuxBatch,
} from '../api';

// Compact segmented progress bar for VAT / Luxembourg package checklists.
// One segment per checklist item, filled when the item is complete.
//
// Hover: shows a portal-rendered popover with each item's status (so it
// can't be clipped by Kanban-card overflow).
// Click: opens an interactive modal where the user can toggle items
// without leaving the page. Edits POST to the package-level endpoint
// and update local state optimistically.
function ChecklistProgressBar({ progress: progressProp, invoiceStatus }) {
  const wrapRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const [popoverPos, setPopoverPos] = useState({ left: 0, top: 0 });
  const [modalOpen, setModalOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Track items locally so toggles in the modal reflect immediately
  // before a parent refresh. Reset whenever the prop's identity changes.
  useEffect(() => {
    setItems(Array.isArray(progressProp?.items) ? progressProp.items : []);
  }, [progressProp]);

  const updatePopoverPos = useCallback(() => {
    if (!wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    const POPOVER_WIDTH = 260;
    const POPOVER_HEIGHT_EST = 180;
    // Default: above the bar, left-aligned to it.
    let top = r.top - POPOVER_HEIGHT_EST - 6;
    let left = r.left;
    // Flip below if not enough room above.
    if (top < 8) top = r.bottom + 6;
    // Keep within viewport horizontally.
    if (left + POPOVER_WIDTH > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - POPOVER_WIDTH - 8);
    }
    if (left < 8) left = 8;
    setPopoverPos({ left, top });
  }, []);

  useEffect(() => {
    if (!hovered) return undefined;
    updatePopoverPos();
    window.addEventListener('scroll', updatePopoverPos, true);
    window.addEventListener('resize', updatePopoverPos);
    return () => {
      window.removeEventListener('scroll', updatePopoverPos, true);
      window.removeEventListener('resize', updatePopoverPos);
    };
  }, [hovered, updatePopoverPos]);

  if (!progressProp) return null;
  const total = progressProp.items_total ?? items.length;
  if (!total) return null;
  const completed = items.filter((it) => it.completed).length;
  const kind = progressProp.kind;
  const batchId = progressProp.batch_id;
  const canToggle = !!(kind && batchId && items.every((it) => it.id));

  const stop = (e) => {
    e.stopPropagation();
  };

  const openModal = (e) => {
    stop(e);
    if (!canToggle) return;
    setHovered(false);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setBusyId(null);
    setSubmitError(null);
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      const fn = kind === 'vat' ? submitVatBatch : submitLuxBatch;
      await fn(batchId);
      // The board's SSE listener will refetch invoices; close the modal.
      setModalOpen(false);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Submit failed';
      setSubmitError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const openDashboard = () => {
    window.location.href = kind === 'lux' ? '/luxembourg' : '/vat';
  };

  const handleToggle = async (item) => {
    if (busyId) return;
    setBusyId(item.id);
    const next = !item.completed;
    // Optimistic local update so the UI feels instant.
    setItems((prev) =>
      prev.map((it) => (it.id === item.id ? { ...it, completed: next } : it))
    );
    try {
      const fn =
        kind === 'vat'
          ? toggleVatBatchChecklistItem
          : toggleLuxBatchChecklistItem;
      await fn(batchId, item.id, next);
    } catch (err) {
      // Roll back on error.
      setItems((prev) =>
        prev.map((it) => (it.id === item.id ? { ...it, completed: !next } : it))
      );
      // eslint-disable-next-line no-console
      console.error('Failed to toggle checklist item', err);
    } finally {
      setBusyId(null);
    }
  };

  const popoverNode = hovered ? (
    <div
      className="checklist-progress-popover"
      role="tooltip"
      style={{ left: popoverPos.left, top: popoverPos.top }}
    >
      <div className="checklist-progress-popover-header">
        Checklist {completed} / {total}
        {canToggle && (
          <span className="checklist-progress-popover-hint"> · click to edit</span>
        )}
      </div>
      <ul className="checklist-progress-popover-list">
        {items.map((it, idx) => (
          <li key={it.id ?? idx} className={it.completed ? 'done' : 'pending'}>
            <span className="checklist-progress-popover-mark">
              {it.completed ? '✓' : '○'}
            </span>
            <span className="checklist-progress-popover-label">{it.label}</span>
          </li>
        ))}
      </ul>
    </div>
  ) : null;

  const modalNode = modalOpen ? (
    <div className="checklist-modal-overlay" onClick={closeModal}>
      <div
        className="checklist-modal"
        onClick={stop}
        role="dialog"
        aria-modal="true"
      >
        <div className="checklist-modal-header">
          <h2>
            {kind === 'lux' ? 'Luxembourg' : 'VAT'} package checklist
          </h2>
          <button
            type="button"
            className="checklist-modal-close"
            onClick={closeModal}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="checklist-modal-progress">
          {completed} / {total} complete
        </div>
        <ul className="checklist-modal-list">
          {items.map((it) => (
            <li key={it.id} className={it.completed ? 'done' : ''}>
              <label>
                <input
                  type="checkbox"
                  checked={!!it.completed}
                  disabled={busyId === it.id}
                  onChange={() => handleToggle(it)}
                />
                <span className="checklist-modal-label">{it.label}</span>
              </label>
            </li>
          ))}
        </ul>
        {submitError && (
          <div className="checklist-modal-error">{submitError}</div>
        )}
        <div className="checklist-modal-footer">
          <button
            type="button"
            className="btn btn-link"
            onClick={openDashboard}
          >
            {kind === 'lux' ? 'Open Luxembourg Dashboard' : 'Open VAT Dashboard'}
          </button>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            className="btn btn-secondary"
            onClick={closeModal}
            disabled={submitting}
          >
            Done
          </button>
          {(() => {
            const allDone = completed === total && total > 0;
            const isBatched =
              invoiceStatus === 'vat_batched' ||
              invoiceStatus === 'lux_batched';
            const enabled = allDone && isBatched && !submitting;
            let title;
            if (!allDone) {
              title = `Complete the checklist (${completed}/${total}) first`;
            } else if (!isBatched) {
              title =
                kind === 'lux'
                  ? 'Generate the Luxembourg package first on the dashboard'
                  : 'Generate the VAT package first on the dashboard';
            } else {
              title = 'Mark this package as Sent to AP';
            }
            return (
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={!enabled}
                title={title}
              >
                {submitting ? 'Submitting…' : 'Mark as Sent to AP'}
              </button>
            );
          })()}
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      <div
        ref={wrapRef}
        className="checklist-progress-wrap"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={openModal}
      >
        <div
          className="checklist-progress-bar"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={completed}
          aria-label={`Checklist ${completed} of ${total} complete`}
        >
          {items.map((it, idx) => (
            <span
              key={it.id ?? idx}
              className={`checklist-progress-segment${it.completed ? ' done' : ''}`}
            />
          ))}
        </div>
      </div>
      {popoverNode && ReactDOM.createPortal(popoverNode, document.body)}
      {modalNode && ReactDOM.createPortal(modalNode, document.body)}
    </>
  );
}

export default ChecklistProgressBar;
